import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  Paper
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import type { ChangeEventDiff } from '../../types/changeDetection.types';

interface ChangeDiffViewerProps {
  diff: ChangeEventDiff;
}

const formatFieldName = (field: string): string => {
  const fieldNames: Record<string, string> = {
    title: 'Title',
    metaDescription: 'Meta Description',
    canonical: 'Canonical URL',
    robotsMeta: 'Robots Meta',
    h1: 'H1 Heading',
    wordCount: 'Word Count',
    schema: 'Schema',
    template: 'Template'
  };
  return fieldNames[field] || field;
};

const truncateText = (text: string | null, maxLength: number = 50): string => {
  if (!text) return '—';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const ChangeDiffViewer: React.FC<ChangeDiffViewerProps> = ({ diff }) => {
  const fields = Object.entries(diff.fields);

  if (fields.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No changes to display
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Before</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>After</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Change</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map(([field, change]) => {
            const beforeText = change.before || '—';
            const afterText = change.after || '—';
            const isTruncatedBefore = beforeText.length > 50;
            const isTruncatedAfter = afterText.length > 50;

            return (
              <TableRow key={field} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {formatFieldName(field)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={isTruncatedBefore ? beforeText : ''} arrow>
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                      {truncateText(beforeText)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={isTruncatedAfter ? afterText : ''} arrow>
                    <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-word' }}>
                      {truncateText(afterText)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  {change.delta !== undefined ? (
                    <Chip
                      label={`${change.delta > 0 ? '+' : ''}${change.delta}`}
                      color={change.delta > 0 ? 'success' : change.delta < 0 ? 'error' : 'default'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : (
                    <Chip
                      label="Modified"
                      icon={<EditIcon />}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
